// © Microsoft Corporation. All rights reserved.

import { BridgeEventNames, dispatchBridgeEvent } from "../Social.bridge";
import { ContentSocialBarTelemetryObjects, contentSocialBarTelemetryContracts } from "./ContentSocialBar.telemetry";
import { hasThemeBase, isComponentDisabled } from "../utils/themeHelpers";

import { Content } from "@msnews/social-data-service";
import React from "react";
import SocialBarPanel from "../common/social-bar-panel/SocialBarPanel";
import { SocialProps } from "../Social.props";
import { SocialState } from "../Social.state";
import { String } from "typescript-string-operations";
import { addOrUpdateTelemetryObject } from "../Social.telemetry";
import { classNames } from "@microsoft/fast-web-utilities";
import { connect } from "react-redux";
import { enums } from "../utils/enums";
import { getContextData } from "@msnews/core";
import { getFlight } from "../utils/getFlight";
import isValidURL from "../utils/isValidURL";
import { logger } from "@msnews/diagnostics";
import { selectContentId } from "../utils/contentIdKey";

interface ContentSocialBarState {
    loaded: boolean;
}

export interface ContentSocialBarProps extends SocialProps {
    content?: Content;
}

const mapStateToProps = function (state: SocialState, props: SocialProps) {
    const contentId = selectContentId(state.contentIds, props.id, props.market);
    return {
        content: state.contents && state.contents[contentId]
    };
};

export class ContentSocialBar extends React.Component<ContentSocialBarProps, ContentSocialBarState> {
    containerRef: React.RefObject<HTMLDivElement>;
    telemetryObjects: ContentSocialBarTelemetryObjects;
    isReactionsDisabled: boolean;

    constructor(props) {
        super(props);

        this.state = {
            loaded: false
        };
        this.containerRef = React.createRef<HTMLDivElement>();
        this.isReactionsDisabled = this.isCommentsOnly();
    }

    componentDidMount() {
        this.props.socialBarConnector.onSocialBarComponentConnected({ id: this.props.id, market: this.props.market });
        this.disableSuperAppBodyScrollbar();
    }

    componentDidUpdate(prevProps) {
        if (this.props.id != null && this.props.market != null && prevProps.id != this.props.id || prevProps.market != this.props.market) {
            this.props.socialBarConnector.onSocialBarComponentConnected({ id: this.props.id, market: this.props.market });
        }
    }

    render() {
        const { t, managedClasses, users, responsiveClass, mainTelemetryObject, id, content, theme } = this.props;
        const isSuperApp = hasThemeBase(theme, enums.theme.superappBase);
        if (!content) { return null; }
        const { commentStatus, reactionCounts, totalCommentCount, rating, friendRatings, commentWhiteListSegments, commentBlackListSegments, url } = content;

        const showCommentsButton = commentStatus != "Off" &&
            isValidURL(getContextData(this.props.requestContext.data.locationHref, "locationHref"), url, commentWhiteListSegments, commentBlackListSegments);

        /** Generate Telemetry Objects */
        this.telemetryObjects = {
            container: addOrUpdateTelemetryObject(mainTelemetryObject, contentSocialBarTelemetryContracts.container, { id }, this.telemetryObjects && this.telemetryObjects.container),
            reactionBreakdown: addOrUpdateTelemetryObject(mainTelemetryObject, contentSocialBarTelemetryContracts.reactionBreakdown, { id }, this.telemetryObjects && this.telemetryObjects.reactionBreakdown)
        };

        // Reaction Counts
        const totalReactionCount = reactionCounts ? Object.values(reactionCounts).reduce(($sum, $count) => $sum + $count, 0) : 0;
        const reactionCountsVisible = totalReactionCount > 0;
        let label = `${totalReactionCount}`;
        const widthExists = (this.containerRef && this.containerRef.current) ? this.containerRef.current.offsetWidth > 700 : false;
        if (widthExists && users && friendRatings && Object.keys(friendRatings).length > 0) {
            const friendNames = Object.keys(friendRatings).slice(0, 2).map(x => users[x].data.primaryName);
            label = friendNames.join(", ");
            if (totalReactionCount > friendNames.length) {
                label = String.Format(t.SocialBarNamesAndMoreTxt, {
                    count: `${totalReactionCount - friendNames.length}`,
                    names: friendNames.join(", ")
                });
            }
        }

        return (
            <div
                ref={this.containerRef}
                className={classNames(
                    managedClasses.contentSocialBarContainer,
                    responsiveClass,
                    [managedClasses.contentSocialBarSuperAppContainer, isSuperApp]
                )}
            >
                <SocialBarPanel
                    reactionMenuClassName={classNames([managedClasses.contentReactionPopoversSuperAppContainer, isSuperApp && !reactionCountsVisible])}
                    onReaction={this.reactionHandler.bind(this)}
                    replyLabel={<React.Fragment>
                        {String.Format((!totalCommentCount || totalCommentCount > 1) ? t.SocialBarCommentsPlural : t.SocialBarCommentsSingular, {
                            count: (totalCommentCount || "")
                        })}
                    </React.Fragment>}
                    onCommentsClick={this.commentsClickHandler.bind(this)}
                    onReactionsClick={this.onReactionsClick.bind(this)}
                    showCommentsButton={showCommentsButton}
                    reactionCounts={reactionCounts}
                    label={label}
                    reactionsDisabled={this.isReactionsDisabled}
                    {...{t, rating, managedClasses, theme, mainTelemetryObject: this.telemetryObjects && this.telemetryObjects.container, responsiveClass}}
                />
            </div>
        );
    }

    onReactionsClick(e, ref) {
        const contentId = this.props.content && this.props.content.id;
        this.props.popupConnector.openPopup("reactionBreakdownContent", { id: contentId, cornerPosition: {top: "36px"}, focusBackRef: ref,
            parentTelemetryObject: this.telemetryObjects && this.telemetryObjects.reactionBreakdown});
    }

    /* REACTIONS */
    reactionHandler(type) {
        const content = this.props.content;
        if (!content) { return; }

        const sendContentReaction = this.props.socialBarConnector.sendContentReaction;
        const reset = content.rating == type;
        sendContentReaction(content, type, reset);
    }

    /* COMMENTS */
    commentsClickHandler(target) {
        if (isComponentDisabled(this.props.theme, "reactionbreakdowncontent")) {
            dispatchBridgeEvent(BridgeEventNames.commentsClicked);
        } else {
            document.location.href = document.location.href.replace("#comments", "")+"#comments";
        }
        return false;
    }

    disableSuperAppBodyScrollbar() {
        const isSuperApp = hasThemeBase(this.props.theme, enums.theme.superappBase);
        if (isSuperApp) { document.body.style.overflow = "hidden"; }
    }

    isCommentsOnly() {
        return getFlight(enums.flights.contentCommentsOnly);
    }
}

export default connect(mapStateToProps)(ContentSocialBar);